import { DownloadIcon, ExpandIcon, IconButton, PopupIcon } from '@/components/IconButton';
import { FinishedTag } from '@/components/ProcessTab/FinishedTag';
import { IgnoredTag } from '@/components/ProcessTab/IgnoredTag';
import { InProgressTag } from '@/components/ProcessTab/InProgressTag';
import { useShareLink } from '@/stores/shareLink';
import {
  Photo,
  startPanoramaWalkthrough,
  startPhotoOverlay,
  stopPanoramaView,
  stopPhotoOverlay,
  transitionPanoramaWalkthrough,
  useViewer,
} from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { downloadBlob } from '@/utils/download';
import { Progress, toast } from '@skand/ui';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { Vector2, Vector3 } from 'three';

interface ThumbanailProps {
  photo: Photo;
}

export const Thumbnail = ({ photo }: ThumbanailProps) => {
  const api3D = useViewer(state => state.api3D);
  const targetPhoto = useViewer(state => state.targetPhoto);
  const annotationGroups = useViewer(state => state.annotationGroups);
  const viewer2DSettings = useViewer(state => state.viewer2DSettings);
  const loadingMutex = useViewer(state => state.loadingMutex);
  const enabledDraw3D = useViewer(state => state.enabledDraw3D);

  const selectedPhotoGroupIds = useShareLink(state => state.selectedPhotoGroupIds);
  const selectedImageIdByImageViewer = useShareLink(state => state.selectedImageIdByImageViewer);
  const selectedImageIdByLayers = useShareLink(state => state.selectedImageIdByLayers);

  // Process related states
  const targetProcess = useViewer(state => state.targetProcess);
  const processImage = targetProcess?.images.get(photo.id);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState(0);

  const [isFullScreenTooltip, setIsFullScreenTooltip] = useState(false);
  const [isPopupTooltip, setIsPopupTooltip] = useState(false);
  const [isDownloadTooltip, setIsDownloadTooltip] = useState(false);

  const [thumbnailSize, setThumbnailSize] = useState<Vector2>(new Vector2());
  const [containerSize, setContainerSize] = useState<Vector2>(new Vector2());

  // Fetch the url and download the blob to render the thumbnail image
  useEffect(() => {
    const downloadThumbnail = async () => {
      try {
        // Fallback to the main url if the thumbnail is not available
        let url = photo.thumbnailUrl;
        if (!url) {
          url = photo.url;
        }
        if (url) {
          const imgBlob = await downloadBlob(url, setDownloadProgress);
          setObjectUrl(window.URL.createObjectURL(imgBlob));
        }
      } catch (error) {
        toast({ type: 'warn', message: `${error}`, lifespan: 10000 });
      }
    };
    downloadThumbnail();
  }, [photo.thumbnailUrl, photo.url]);

  // Handle downloading the image
  const handleDownload = async () => {
    if (photo.url) {
      window.open(photo.url);
    }
  };

  // Handle opening the image popup
  const handlePopup = async () => {
    useViewer.setState(prev => {
      const popupPhotos = new Set(prev.popupPhotos);
      popupPhotos.add(photo);
      return { popupPhotos };
    });
  };

  // Handle opening the image popup
  const handleFullScreen = async () => {
    if (!loadingMutex) {
      stopPhotoOverlay();
      stopPanoramaView();

      if (photo?.type === 'photo2D') {
        await startPhotoOverlay(photo, true);
      }
    }
  };

  // Handle selecting the image
  const handleSelect = async () => {
    const { enabled2D } = useViewer.getState();
    if (selectedPhotoGroupIds) {
      useShareLink.setState({ selectedImageIdByImageViewer: photo.id });
    }
    if (api3D && !loadingMutex) {
      const walkthrough = api3D.panorama.getWalkthrough();
      if (photo.type === 'photo2D') {
        stopPhotoOverlay();
        await startPhotoOverlay(photo, false, !enabled2D);
      } else if (!walkthrough.isActive()) {
        await startPanoramaWalkthrough(photo);
      } else if (photo.widget) {
        await transitionPanoramaWalkthrough(photo);
      }
    }
  };

  // Update image size on load
  const handleImageLoad = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    const img = e.target as HTMLImageElement;
    setThumbnailSize(new Vector2(img.naturalWidth, img.naturalHeight));
    setContainerSize(new Vector2(img.width, img.height));
  };

  // List annotations associated with the photo
  const annotations = annotationGroups
    .flatMap(group => group.annotations)
    .filter(annotation => annotation.photo === photo);
  const isSketch2DAvailable = annotations.length > 0;
  const isSketch3DAvailable = annotations.find(annotation => annotation.sketch3D);
  const isTargetPhotoLoading = downloadProgress > 0 && downloadProgress < 1 && objectUrl === null;

  // Crosshair marker for projected sort point
  const CrosshairMarker = useCallback(() => {
    if (
      !api3D ||
      !viewer2DSettings.showSortPoint ||
      !photo.widget ||
      photo.type !== 'photo2D' ||
      photo.cameraModelId === undefined ||
      enabledDraw3D
    )
      return;

    const cameraModel = photo.group.cameraModels.get(photo.cameraModelId);
    if (!cameraModel) return;

    const loaderPhoto = {
      fileName: photo.name,
      position: photo.widget.getPosition(),
      rotation: photo.widget.getRotation(),
      cameraId: photo.cameraModelId,
    };

    // Check if point is in FOV range
    const expectedDir = new Vector3(0, 0, -1).applyQuaternion(loaderPhoto.rotation).normalize();
    const receivedDir = new Vector3()
      .subVectors(viewer2DSettings.sortPoint, loaderPhoto.position)
      .normalize();
    const fov = Math.atan(cameraModel.sensorSize / (2 * cameraModel.focalLength));
    const inDirection = expectedDir.dot(receivedDir) > Math.cos(fov);
    if (!inDirection) return;

    // Calculate the scale factors
    const thumbnailScaleX = thumbnailSize.x / cameraModel.imageSize.x;
    const thumbnailScaleY = thumbnailSize.y / cameraModel.imageSize.y;

    const containerScaleX = containerSize.x / thumbnailSize.x;
    const containerScaleY = containerSize.y / thumbnailSize.y;
    const containerScale = Math.max(containerScaleX, containerScaleY);

    // Calculate the crop offsets
    const cropX = (thumbnailSize.x * containerScale - containerSize.x) / 2;
    const cropY = (thumbnailSize.y * containerScale - containerSize.y) / 2;

    // Compute marker coordinates on img
    const projected = api3D.deproject.project(viewer2DSettings.sortPoint, loaderPhoto, cameraModel);
    const markerX = projected.x * thumbnailScaleX * containerScale - cropX;
    const markerY = projected.y * thumbnailScaleY * containerScale - cropY;

    return (
      <div
        className="i-skand-closesmall text-20px color-alert-300"
        style={{
          position: 'absolute',
          left: `${markerX}px`,
          top: `${markerY}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 2,
        }}
      />
    );
  }, [
    api3D,
    photo,
    viewer2DSettings.sortPoint,
    viewer2DSettings.showSortPoint,
    thumbnailSize,
    containerSize,
    enabledDraw3D,
  ]);

  return (
    <div className="mb-3">
      <div
        className={cn(
          'w-full',
          'h-200px',
          'bg-zinc-800',
          'relative',
          'mb-1',
          'box-border',
          'b-rounded-1',
          'overflow-hidden',
          'b-primary-400',
          'b-2px',
          (targetPhoto === photo ||
            selectedImageIdByImageViewer === photo.id ||
            selectedImageIdByLayers === photo.id) &&
            'b-solid',
        )}
      >
        {isTargetPhotoLoading && (
          <div
            className={cn('flex', 'h-full', 'absolute', 'w-full', 'justify-center', 'items-center')}
          >
            <Progress className="w-153px" progress={downloadProgress * 100} />
          </div>
        )}
        <div
          className={cn(
            'flex',
            'justify-between',
            'p-8px',
            'absolute',
            'w-full',
            'pointer-events-none',
          )}
        >
          <div className={cn('flex', 'justify-between')}>
            {processImage && (
              <div className="mr-8px">
                {processImage.status === 'created' && <InProgressTag />}
                {processImage.status === 'checked' && <FinishedTag />}
                {processImage.status === 'ignored' && <IgnoredTag />}
              </div>
            )}
            {isSketch2DAvailable && (
              <div
                className={cn(
                  'i-skand-annotation2doutlined',
                  'text-16px',
                  'mr-8px',
                  'text-neutral-400',
                )}
              />
            )}
            {isSketch3DAvailable && (
              <div
                className={cn('i-skand-annotation3doutlined', 'text-16px', 'text-neutral-400')}
              />
            )}
          </div>
          <div className="z-10 flex flex-row">
            {photo.type === 'photo2D' && (
              <div>
                <IconButton
                  buttonIcon={<ExpandIcon className="h-16px w-16px" />}
                  buttonState="active"
                  className={cn(
                    'hover:cursor-pointer',
                    'border-0',
                    'bg-transparent',
                    'pointer-events-auto',
                    'hover:text-primary-400',
                  )}
                  onClick={handleFullScreen}
                  onMouseEnter={() => setIsFullScreenTooltip(true)}
                  onMouseLeave={() => setIsFullScreenTooltip(false)}
                />{' '}
                {isFullScreenTooltip && (
                  <span
                    className={cn(
                      'absolute',
                      'translate-x-[calc(-100%+30px)]',
                      'translate-y-25%',
                      'w-auto',
                      'bg-neutral-800',
                      'text-neutral-100',
                      'typo-text-small',
                      'px-2',
                      'py-1',
                      'rounded-1',
                      'gap-10px',
                      'whitespace-nowrap',
                    )}
                  >
                    Open in 2D full screen
                  </span>
                )}
              </div>
            )}
            <div>
              <IconButton
                buttonIcon={<PopupIcon />}
                buttonState="active"
                className={cn(
                  'hover:cursor-pointer',
                  'border-0',
                  'bg-transparent',
                  'pointer-events-auto',
                  'hover:text-primary-400',
                )}
                onClick={handlePopup}
                onMouseEnter={() => setIsPopupTooltip(true)}
                onMouseLeave={() => setIsPopupTooltip(false)}
              />
              {isPopupTooltip && (
                <span
                  className={cn(
                    'absolute',
                    'translate-x-[calc(-100%+30px)]',
                    'translate-y-25%',
                    'w-auto',
                    'bg-neutral-800',
                    'text-neutral-100',
                    'typo-text-small',
                    'px-2',
                    'py-1',
                    'rounded-1',
                    'gap-10px',
                    'whitespace-nowrap',
                  )}
                >
                  Open in pop-out
                </span>
              )}
            </div>
            <div>
              <IconButton
                buttonIcon={<DownloadIcon />}
                buttonState="active"
                className={cn(
                  'hover:cursor-pointer',
                  'border-0',
                  'bg-transparent',
                  'pointer-events-auto',
                  'hover:text-primary-400',
                )}
                onClick={handleDownload}
                onMouseEnter={() => setIsDownloadTooltip(true)}
                onMouseLeave={() => setIsDownloadTooltip(false)}
              />
              {isDownloadTooltip && (
                <span
                  className={cn(
                    'absolute',
                    'translate-x-[calc(-100%+30px)]',
                    'translate-y-25%',
                    'w-auto',
                    'bg-neutral-800',
                    'text-neutral-100',
                    'typo-text-small',
                    'px-2',
                    'py-1',
                    'rounded-1',
                    'gap-10px',
                    'whitespace-nowrap',
                  )}
                >
                  Download image
                </span>
              )}
            </div>
          </div>
        </div>
        {objectUrl && (
          <div>
            <img
              className={cn(
                'w-full',
                'h-full',
                'object-cover',
                'cursor-pointer',
                'block',
                'max-w-full',
              )}
              onClick={handleSelect}
              onLoad={handleImageLoad}
              src={objectUrl}
            />
            <CrosshairMarker />
          </div>
        )}
      </div>
      <div
        className={cn(
          'typo-text-small',
          'color-neutral-9',
          'text-ellipsis',
          'overflow-hidden',
          'whitespace-nowrap',
        )}
      >
        {photo.group.name} | {photo.name}
      </div>
    </div>
  );
};
